import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_SEARCH_HERO,
  HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_BY_TYPE,
  buildHomepageSearchPath,
  type HomepagePopularSearch,
  type HomepageSearchType,
  type SearchHeroContent,
} from '../../../shared/homepage/searchFirstDefaults';
import { HOMEPAGE_HERO_BG } from '../../../shared/homepage/homepageLayout';
import { trackEvent, trackFormError, trackFormSuccess } from '../../utils/analytics';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import { getFixedMenuProps } from '../../utils/materialUIDropdownUtils';
import { useMaterialUIDropdownClose } from '../../hooks/useMaterialUIDropdownClose';
import { logger } from '../../utils/logger';

export type SearchHeroProps = {
  content?: Partial<SearchHeroContent> | null;
  /** @deprecated Prefer popularSearchesByType; used as events fallback. */
  popularSearches?: HomepagePopularSearch[];
  popularSearchesByType?: Partial<Record<HomepageSearchType, HomepagePopularSearch[]>>;
  loadingPopular?: boolean;
};

type AutocompleteSuggestion = {
  entityType: HomepageSearchType;
  id: string;
  label: string;
  subtitle?: string | null;
  href: string;
};

const ENTITY_TYPES: Array<{ value: HomepageSearchType; label: string }> = [
  { value: 'events', label: 'Events' },
  { value: 'instructors', label: 'Instructors' },
  { value: 'agencies', label: 'Agencies' },
  { value: 'venues', label: 'Venues' },
  { value: 'courses', label: 'Course Catalog' },
];

async function logHomepageSearch(payload: {
  query: string;
  searchType: HomepageSearchType;
  source: 'homepage' | 'chip' | 'autocomplete';
}): Promise<void> {
  try {
    await fetch('/api/homepage/search-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Non-blocking
  }
}

const SearchHero: React.FC<SearchHeroProps> = ({
  content,
  popularSearches = [],
  popularSearchesByType,
  loadingPopular = false,
}) => {
  const navigate = useNavigate();
  const brand = content?.brand || DEFAULT_SEARCH_HERO.brand;
  const mission = content?.mission || DEFAULT_SEARCH_HERO.mission;
  const support = content?.support || DEFAULT_SEARCH_HERO.support;
  const cmsPlaceholder = content?.placeholder || DEFAULT_SEARCH_HERO.placeholder;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchLocation, setSearchLocation] = useState('');
  const [searchType, setSearchType] = useState<HomepageSearchType>('events');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [stateSelectOpen, setStateSelectOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useMaterialUIDropdownClose([{ isOpen: stateSelectOpen, setOpen: setStateSelectOpen }]);

  const chipsForType =
    popularSearchesByType?.[searchType] ??
    (searchType === 'events' ? popularSearches : []);

  const typePlaceholders = HOMEPAGE_SEARCH_PLACEHOLDER_EXAMPLES_BY_TYPE[searchType] || [];
  const rotatingExamples =
    searchType === 'events'
      ? [cmsPlaceholder, ...typePlaceholders.filter((p) => p !== cmsPlaceholder)]
      : [...typePlaceholders];
  const activePlaceholder = rotatingExamples[placeholderIndex % Math.max(rotatingExamples.length, 1)];

  useEffect(() => {
    setPlaceholderIndex(0);
    setSuggestions([]);
  }, [searchType]);

  useEffect(() => {
    if (inputFocused || searchQuery.trim()) return undefined;
    const id = setInterval(() => {
      setPlaceholderIndex((i) => i + 1);
    }, 4000);
    return () => clearInterval(id);
  }, [inputFocused, searchQuery, searchType]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSuggestLoading(false);
      return undefined;
    }
    setSuggestLoading(true);
    debounceRef.current = setTimeout(() => {
      void (async () => {
        try {
          const params = new URLSearchParams({
            q,
            types: searchType,
            limit: '8',
          });
          const res = await fetch(`/api/homepage/autocomplete?${params.toString()}`);
          if (!res.ok) {
            setSuggestions([]);
            return;
          }
          const json = await res.json();
          const rows = Array.isArray(json?.data?.suggestions) ? json.data.suggestions : [];
          setSuggestions(rows as AutocompleteSuggestion[]);
        } catch {
          setSuggestions([]);
        } finally {
          setSuggestLoading(false);
        }
      })();
    }, 220);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, searchType]);

  const navigateSearch = (
    query: string,
    opts?: {
      fromChip?: boolean;
      fromAutocomplete?: boolean;
      chipLabel?: string;
      typeOverride?: HomepageSearchType;
      hrefOverride?: string;
    },
  ) => {
    const type = opts?.typeOverride || searchType;
    const path =
      opts?.hrefOverride ||
      buildHomepageSearchPath({
        query,
        location: searchLocation || undefined,
        searchType: type,
      });
    try {
      void logHomepageSearch({
        query,
        searchType: type,
        source: opts?.fromChip ? 'chip' : opts?.fromAutocomplete ? 'autocomplete' : 'homepage',
      });
      if (opts?.fromChip) {
        trackEvent({
          action: 'homepage_popular_search_click',
          category: 'engagement',
          label: opts.chipLabel || query,
          custom_parameters: { query, searchType: type, searchLocation },
        });
      } else {
        trackEvent({
          action: 'search_submitted',
          category: 'engagement',
          label: opts?.fromAutocomplete ? 'homepage_autocomplete' : 'homepage_search',
          custom_parameters: { searchQuery: query, searchLocation, searchType: type },
        });
      }
      trackFormSuccess({
        searchQuery: query,
        searchLocation,
        searchType: type,
        destination: path,
      });
      navigate(path);
    } catch (error) {
      trackFormError(error);
      logger.error('Homepage search error:', error, {});
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigateSearch(searchQuery);
  };

  return (
    <Box
      component="section"
      aria-label="Search law enforcement training"
      sx={(theme) => ({
        minHeight: { xs: '62vh', md: 640 },
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        bgcolor: theme.palette.mode === 'dark' ? 'background.default' : HOMEPAGE_HERO_BG,
        color: 'text.primary',
        py: { xs: 6, md: 10 },
        px: AGENCY_SHELL_HORIZONTAL_PX,
      })}
    >
      <Container maxWidth="md" disableGutters>
        {/* Site name for xs/sm only — header is icon-only below md; md+ already shows full lockup. */}
        <Typography
          variant="overline"
          sx={{
            display: { xs: 'block', md: 'none' },
            textAlign: 'center',
            letterSpacing: '0.12em',
            fontWeight: 700,
            color: 'primary.main',
            mb: 1.5,
          }}
        >
          {brand}
        </Typography>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.85rem', sm: '2.5rem', md: '3.25rem' },
            letterSpacing: '-0.03em',
            mb: 1.5,
            textAlign: 'center',
            lineHeight: 1.15,
            color: 'text.primary',
          }}
        >
          {mission}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            mb: { xs: 3.5, md: 4.5 },
            color: 'text.secondary',
            fontSize: { xs: '1rem', md: '1.15rem' },
            lineHeight: 1.6,
            maxWidth: 640,
            mx: 'auto',
          }}
        >
          {support}
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          data-hs-form-id="homepage-search-form"
          data-hs-form-name="Homepage Search"
          data-hs-form-type="search"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 4,
            p: { xs: 2, sm: 3 },
            boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            justifyContent="center"
            sx={{ mb: 2.5 }}
            role="group"
            aria-label="Search category"
          >
            {ENTITY_TYPES.map((t) => {
              const selected = searchType === t.value;
              return (
                <Chip
                  key={t.value}
                  label={t.label}
                  clickable
                  onClick={() => setSearchType(t.value)}
                  aria-pressed={selected}
                  sx={(theme) => ({
                    px: 1.25,
                    height: 36,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    borderRadius: 999,
                    bgcolor: selected
                      ? 'primary.main'
                      : theme.palette.mode === 'dark'
                        ? 'action.selected'
                        : 'grey.100',
                    color: selected
                      ? theme.palette.primary.contrastText
                      : 'text.primary',
                    border: '1px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    transition: 'background-color 0.2s ease, transform 0.15s ease',
                    '&:hover': {
                      bgcolor: selected
                        ? 'primary.dark'
                        : theme.palette.mode === 'dark'
                          ? 'action.hover'
                          : 'grey.200',
                      transform: 'translateY(-1px)',
                    },
                  })}
                />
              );
            })}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <Autocomplete
              freeSolo
              fullWidth
              options={suggestions}
              filterOptions={(x) => x}
              loading={suggestLoading}
              inputValue={searchQuery}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input' || reason === 'clear') setSearchQuery(value);
              }}
              onChange={(_e, value) => {
                if (typeof value === 'string') {
                  navigateSearch(value);
                  return;
                }
                if (value && typeof value === 'object') {
                  navigateSearch(value.label, {
                    fromAutocomplete: true,
                    typeOverride: value.entityType,
                    hrefOverride: value.href,
                  });
                }
              }}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
              renderOption={(props, option) => (
                <li {...props} key={`${option.entityType}-${option.id}`}>
                  <Box sx={{ py: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.subtitle || option.entityType}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  placeholder={activePlaceholder}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'Search training query',
                    'data-hs-form-field': 'search-query',
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 28, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {suggestLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={(theme) => ({
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                      bgcolor:
                        theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                      color: 'text.primary',
                      fontSize: '1.15rem',
                      minHeight: 64,
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: 'text.secondary',
                      opacity: 1,
                    },
                  })}
                />
              )}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              data-hs-form-submit="homepage-search-form"
              sx={{
                minWidth: { sm: 148 },
                height: 64,
                fontWeight: 700,
                borderRadius: 2.5,
                fontSize: '1.05rem',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              Search
            </Button>
          </Stack>

          <Button
            type="button"
            size="small"
            onClick={() => setFiltersOpen((v) => !v)}
            endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />}
            sx={{ mt: 1.5, color: 'text.secondary', textTransform: 'none' }}
            aria-expanded={filtersOpen}
          >
            More filters
          </Button>
          <Collapse in={filtersOpen}>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
              <InputLabel id="sf-search-state-label">State</InputLabel>
              <Select
                labelId="sf-search-state-label"
                value={searchLocation}
                label="State"
                open={stateSelectOpen}
                onOpen={() => setStateSelectOpen(true)}
                onClose={() => setStateSelectOpen(false)}
                onChange={(e) => setSearchLocation(e.target.value)}
                MenuProps={getFixedMenuProps(() => setStateSelectOpen(false))}
              >
                <MenuItem value="">All States</MenuItem>
                <MenuItem value="TX">Texas</MenuItem>
                <MenuItem value="CA">California</MenuItem>
                <MenuItem value="NY">New York</MenuItem>
                <MenuItem value="FL">Florida</MenuItem>
                <MenuItem value="IL">Illinois</MenuItem>
              </Select>
            </FormControl>
          </Collapse>
        </Box>

        <Box sx={{ mt: 3.5 }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              textAlign: 'center',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              fontWeight: 700,
            }}
          >
            Popular searches
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1}
            justifyContent="center"
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              // Reserve space so chip load does not inflate CLS under the hero search form
              minHeight: { xs: 88, sm: 48 },
              alignContent: 'flex-start',
            }}
          >
            {loadingPopular && chipsForType.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" width={140} height={36} sx={{ borderRadius: 999 }} />
                ))
              : chipsForType.map((chip) => (
                  <Box component="li" key={chip.id} sx={{ display: 'inline' }}>
                    <Chip
                      label={chip.label}
                      onClick={() =>
                        navigateSearch(chip.query, { fromChip: true, chipLabel: chip.label })
                      }
                      sx={(theme) => ({
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        fontWeight: 600,
                        maxWidth: { xs: '100%', sm: 280 },
                        height: 'auto',
                        py: 0.75,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 999,
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          display: 'block',
                          lineHeight: 1.35,
                          py: 0.25,
                        },
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? 'action.hover'
                              : 'primary.50',
                          borderColor: 'primary.light',
                        },
                      })}
                    />
                  </Box>
                ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default SearchHero;
